import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";

import "./index.css";
import { AuthGate } from "@/components/AuthGate";
import { RequirePermission } from "@/components/RequirePermission";
import { landingPathFor } from "@/lib/landing";
import { useAuthStore } from "@/store/auth-store";
import { ThemeProvider } from "@/components/ThemeProvider";
import { Toaster } from "@/components/ui/toast";
import { LoginPage } from "@/pages/LoginPage";
import { AdminPage } from "@/pages/AdminPage";
import { CandidateTestsPage } from "@/pages/CandidateTestsPage";
import { TestAttemptPage } from "@/pages/TestAttemptPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // Off by default and re-enabled per query. A background refetch
      // under someone's cursor is worse than slightly old data on most
      // screens; the two admin lists that report what candidates are
      // doing right now opt back in explicitly.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// Global safety guard for every <input type="number"> in the app.
//
// A focused number input changes its value when the mouse wheel scrolls
// over it (Chrome/Edge/Firefox default). On a long worksheet this silently
// writes measurements nobody typed — an empty size box (min=0) that catches
// a wheel-down snaps to "0 mm", which then prints on the report. Blur the
// number input on wheel so the page scrolls and the value is never touched.
// Capture phase runs before the input's native increment. Nothing is asked
// of the user — scrolling, clicking and tabbing all stay safe.
document.addEventListener(
  "wheel",
  (e) => {
    const el = e.target as HTMLElement | null;
    if (el instanceof HTMLInputElement && el.type === "number") {
      el.blur();
    }
  },
  { capture: true, passive: true },
);

/** Redirect to the current user's landing page (see lib/landing.ts). */
function LandingRedirect() {
  const role = useAuthStore((s) => s.user?.role ?? null);
  return <Navigate to={landingPathFor(role)} replace />;
}

const root = document.getElementById("root");
if (!root) throw new Error("#root missing in index.html");

// Vite's base ("/") lands here as BASE_URL. Feeding it to the router
// means every <Route path="/tests"> and every navigate("/login") stays
// written as a root-relative path, and if this app is ever moved under a
// path prefix it picks that up for free. react-router wants the basename
// without a trailing slash, which for "/" means the empty string.
const basename = import.meta.env.BASE_URL.replace(/\/$/, "");

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={basename}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <AuthGate>
                  <Outlet />
                </AuthGate>
              }
            >
              {/* Staff. Gated on can_manage_tests rather than a
                  general "is staff" idea, because that permission is
                  exactly what the console is for -- a reviewer holds
                  it, a candidate never can. */}
              <Route element={<RequirePermission permission="canManageTests" />}>
                <Route path="/admin" element={<AdminPage />} />
              </Route>

              {/* The candidate surface. A candidate holds can_take_test
                  and NOTHING else, so these are the only two pages they
                  can reach -- and there is no clinical surface in this
                  system for them to be kept out of, which is the whole
                  reason it was extracted. */}
              <Route element={<RequirePermission permission="canTakeTest" />}>
                <Route path="/tests" element={<CandidateTestsPage />} />
                <Route path="/tests/:attemptId" element={<TestAttemptPage />} />
              </Route>
            </Route>
            {/* Unknown path -> wherever this user belongs. Not a bare
                "/admin": that is not every role's home. */}
            <Route path="*" element={<LandingRedirect />} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
